import { PaginatedResponse, SingleResponse } from '@/app/shared/types';
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { APP_CONFIG } from '@environments/environment';
import { ResponseSingleWrapper, ResponsePaginatedWrapper } from '@/app/shared/dto';
import { map, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export abstract class ApiService {
    protected readonly apiUrl = APP_CONFIG.apiUrl;
    protected readonly http = inject(HttpClient);

    protected abstract get basePath(): string;

    protected get<T>(endpoint: string, params?: HttpParams | Record<string, any>): SingleResponse<T> {
        return this.http.get<ResponseSingleWrapper<T>>(this.buildUrl(endpoint), { params, withCredentials: true });
    }

    protected getVoid(endpoint: string): Observable<void> {
        return this.http
            .get(this.buildUrl(endpoint), {
                withCredentials: true,
            })
            .pipe(map(() => undefined));
    }

    protected getPaginated<T>(endpoint: string, params?: HttpParams | Record<string, any>): PaginatedResponse<T> {
        return this.http.get<ResponsePaginatedWrapper<T>>(this.buildUrl(endpoint), { params, withCredentials: true });
    }

    protected post<T>(endpoint: string, body?: any): SingleResponse<T> {
        return this.http.post<ResponseSingleWrapper<T>>(this.buildUrl(endpoint), body, {
            withCredentials: true,
        });
    }

    protected postVoid(endpoint: string, body?: any): Observable<void> {
        return this.http
            .post(this.buildUrl(endpoint), body, {
                withCredentials: true,
            })
            .pipe(map(() => undefined));
    }

    protected postPaginated<T>(endpoint: string, body?: any): PaginatedResponse<T> {
        return this.http.post<ResponsePaginatedWrapper<T>>(this.buildUrl(endpoint), body, {
            withCredentials: true,
        });
    }

    protected put<T>(endpoint: string, body?: any): SingleResponse<T> {
        return this.http.put<ResponseSingleWrapper<T>>(this.buildUrl(endpoint), body, {
            withCredentials: true,
        });
    }

    protected putVoid(endpoint: string, body?: any): Observable<void> {
        return this.http
            .put(this.buildUrl(endpoint), body, {
                withCredentials: true,
            })
            .pipe(map(() => undefined));
    }

    protected patch<T>(endpoint: string, body?: any): SingleResponse<T> {
        return this.http.patch<ResponseSingleWrapper<T>>(this.buildUrl(endpoint), body, {
            withCredentials: true,
        });
    }

    protected delete<T>(endpoint: string): SingleResponse<T> {
        return this.http.delete<ResponseSingleWrapper<T>>(this.buildUrl(endpoint), {
            withCredentials: true,
        });
    }

    protected deleteVoid(endpoint: string, options?: any): Observable<void> {
        return this.http
            .delete(this.buildUrl(endpoint), {
                ...options,
                withCredentials: true,
            })
            .pipe(map(() => undefined));
    }

    private buildUrl(endpoint: string): string {
        const base = this.basePath.replace(/^\/|\/$/g, '');
        const path = endpoint.replace(/^\/|\/$/g, '');

        return `${this.apiUrl}/${base}/${path}`;
    }
}
